import { currentCallState, loggedInUser } from "src/apollo/cache";
import { MixpanelActions } from "src/services/mixpanel";

// Define interface for metrics with all possible properties
export interface MixpanelAudioQualityMetricsInterface {
  in_call_state: boolean;
  call_is_live: boolean;
  // Warning information
  warning_type: string;
  warning_message: string;
  warning_severity: string;

  // User and organization information
  user_id?: string;
  organization_id?: string;
  organization_name?: string;
  user_role?: string;

  // Browser and OS details
  browser: string;
  browser_vendor: string;
  browser_version: string;
  platform: string;
  os: string;
  language: string;
  timezone: string;

  // Network connection details
  connection_type?: string;
  connection_effective_type?: string;
  connection_downlink?: number;
  connection_rtt?: number;
  connection_save_data?: boolean;

  // Screen and device details
  screen_resolution: string;
  screen_color_depth: number;
  device_pixel_ratio: number;
  device_memory?: number;
  hardware_concurrency: number;

  // Audio context details
  audio_context_sample_rate: number | null;
  audio_context_latency: number | null;

  // Performance metrics
  timestamp: string;
  page_load_time: number | null;
  memory_usage?: number;

  // Tab visibility
  tab_visible: boolean;

  // Call information
  call_in_progress: boolean;
  call_duration?: number;
  call_status?: string;
  call_direction?: string;
  call_sid?: string;

  // Optional properties that will be added conditionally
  audio_devices?: {
    input_devices: Array<{ deviceId: string; label: string }>;
    output_devices: Array<{ deviceId: string; label: string }>;
    selected_input: string | null;
    selected_output: string | null;
    input_device_count: number;
    output_device_count: number;
  };
  audio_devices_error?: string;

  webrtc_stats?: {
    audio_packets_received?: number;
    audio_packets_lost?: number;
    audio_jitter?: number;
    audio_level?: number;
    audio_codec?: string;
    audio_packets_sent?: number;
    audio_bytes_sent?: number;
    audio_track_muted?: boolean;
  };
  webrtc_stats_error?: string;

  microphone_permission?: string;
  microphone_permission_error?: string;

  error_collecting_metrics?: string;
}

// Map of network warning messages
export const networkWarningTooltips: Record<string, string> = {
  "high-rtt": "High Round Trip Time (RTT) detected. This may cause noticeable audio delays.",
  "low-mos": "Overall network conditions appear to be poor. This can affect call quality.",
  "high-jitter":
    "High jitter detected. This can result in audio quality problems on the call, such as crackling and choppy audio.",
  "high-packet-loss": "High packet loss detected. This can result in choppy audio or a dropped call.",
  "high-packets-lost-fraction": "Significant packet loss detected! This can result in choppy audio or a dropped call.",
  "low-bytes-received": "No data received for 3 seconds. This can affect call quality.",
  "low-bytes-sent": "No data sent for 3 seconds. This can affect call quality.",
  "ice-connectivity-lost": "Connection was lost.",
  "": "Connection appears stable.",
};

/**
 * Collects comprehensive audio quality metrics related to Twilio calls
 *
 * @param twilioWarning - The current Twilio warning string
 * @param callContext - The CallContext object containing call information
 * @returns A promise resolving to an object with all collected metrics
 */
export const collectAudioQualityMetrics = async (
  twilioWarning: string,
  callContext: any,
): Promise<MixpanelAudioQualityMetricsInterface> => {
  const user = loggedInUser();
  const connection =
    (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;

  // Try to get Twilio call info
  const callEntity = callContext?.callEntity;

  // Base metrics
  const metrics: MixpanelAudioQualityMetricsInterface = {
    in_call_state: !!callContext?.callState,
    call_is_live: currentCallState().onCall,
    // Warning information
    warning_type: twilioWarning,
    warning_message: networkWarningTooltips[twilioWarning] || "Unknown warning",
    warning_severity:
      twilioWarning.includes("high-packets-lost") || twilioWarning.includes("ice-connectivity-lost")
        ? "severe"
        : "moderate",

    // User and organization information
    user_id: user?.id,
    organization_id: user?.organization?.id,
    organization_name: user?.organization?.name,
    user_role: user?.role,

    // Browser and OS details
    browser: navigator.userAgent,
    browser_vendor: navigator.vendor,
    browser_version: navigator.appVersion,
    platform: navigator.platform,
    os: navigator.platform,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

    // Network connection details
    connection_type: connection?.type,
    connection_effective_type: connection?.effectiveType,
    connection_downlink: connection?.downlink,
    connection_rtt: connection?.rtt,
    connection_save_data: connection?.saveData,

    // Screen and device details
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    screen_color_depth: window.screen.colorDepth,
    device_pixel_ratio: window.devicePixelRatio,
    device_memory: (navigator as any).deviceMemory,
    hardware_concurrency: navigator.hardwareConcurrency,

    // Audio context details
    audio_context_sample_rate: (window as any).AudioContext ? new (window as any).AudioContext().sampleRate : null,
    audio_context_latency: (window as any).AudioContext ? new (window as any).AudioContext().baseLatency : null,

    // Performance metrics
    timestamp: new Date().toISOString(),
    page_load_time: performance.timing ? performance.timing.loadEventEnd - performance.timing.navigationStart : null,
    memory_usage: (performance as any).memory?.usedJSHeapSize,

    // Tab visibility
    tab_visible: !document.hidden,

    // Call information
    call_in_progress: !!callContext?.callState,
    call_duration: callContext?.timeOnCall,
    call_status: callEntity?.status?.(),
    call_direction: callEntity?.direction,
    call_sid: callContext?.callSid,
  };

  // Audio devices information
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputDevices = devices
      .filter((d) => d.kind === "audioinput")
      .map((d) => ({ deviceId: d.deviceId, label: d.label }));
    const audioOutputDevices = devices
      .filter((d) => d.kind === "audiooutput")
      .map((d) => ({ deviceId: d.deviceId, label: d.label }));

    metrics.audio_devices = {
      input_devices: audioInputDevices,
      output_devices: audioOutputDevices,
      selected_input: callContext?.defaultInput || null,
      selected_output: callContext?.defaultOutput || null,
      input_device_count: audioInputDevices.length,
      output_device_count: audioOutputDevices.length,
    };
  } catch (e) {
    metrics.audio_devices_error = String(e);
  }

  // WebRTC Statistics if available
  try {
    if (callEntity?.peerConnection) {
      const stats = await callEntity.peerConnection.getStats();
      const rtcStats: {
        audio_packets_received?: number;
        audio_packets_lost?: number;
        audio_jitter?: number;
        audio_level?: number;
        audio_codec?: string;
        audio_packets_sent?: number;
        audio_bytes_sent?: number;
        audio_track_muted?: boolean;
      } = {};

      stats.forEach((stat: any) => {
        if (stat.type === "inbound-rtp" && stat.kind === "audio") {
          rtcStats.audio_packets_received = stat.packetsReceived;
          rtcStats.audio_packets_lost = stat.packetsLost;
          rtcStats.audio_jitter = stat.jitter;
          rtcStats.audio_level = stat.audioLevel;
          rtcStats.audio_codec = stat.codecId;
        } else if (stat.type === "outbound-rtp" && stat.kind === "audio") {
          rtcStats.audio_packets_sent = stat.packetsSent;
          rtcStats.audio_bytes_sent = stat.bytesSent;
        } else if (stat.type === "media-source" && stat.kind === "audio") {
          rtcStats.audio_track_muted = stat.muted;
        }
      });

      metrics.webrtc_stats = rtcStats;
    }
  } catch (e) {
    metrics.webrtc_stats_error = String(e);
  }

  // Microphone permissions
  try {
    const micPermission = await navigator.permissions.query({ name: "microphone" as PermissionName });
    metrics.microphone_permission = micPermission.state;
  } catch (e) {
    metrics.microphone_permission_error = String(e);
  }

  return metrics;
};
